import type { DuplicateReferenceHint, TenderInput } from '@didto/domain';
import { describeMatch, formatInTimeZone, formatMinor, referenceKey } from '@didto/domain';
import { Button, Field, Notice, Sheet, Textarea } from '@didto/ui';
import { useState } from 'react';
import type { AppError } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import { hintOf } from '@/lib/orders';
import './pos.css';

/**
 * A payment reference used before (Phase 7B-2, D5, ADR-032). The server never takes it silently and never refuses it
 * outright: the cashier checks the customer's confirmation and says why it is taken again; the payment is then
 * flagged for the owner's review.
 */

export const DUPLICATE_REASON_MAX = 200;

/** The refusal of a capture whose reference was used before, or null for any other refusal. */
export function duplicateOf(e: AppError): DuplicateReferenceHint | null {
  const h = hintOf(e);
  if (e.code !== 'conflict' || h.requires !== 'duplicate_reason') return null;
  const d = h.duplicate_reference as DuplicateReferenceHint | undefined;
  return d && typeof d.reference === 'string' ? d : null;
}

/** The tenders again, the one the server asked about now carrying the cashier's reason. */
export function withDuplicateReason(
  tenders: TenderInput[],
  hint: DuplicateReferenceHint,
  reason: string,
): TenderInput[] {
  const byId = hint.tender_id ? tenders.findIndex((t) => t.id === hint.tender_id) : -1;
  const i =
    byId >= 0
      ? byId
      : tenders.findIndex(
          (t) => !t.duplicate_reason && referenceKey(t.reference) === referenceKey(hint.reference),
        );
  return tenders.map((t, j) => (j === i ? { ...t, duplicate_reason: reason } : t));
}

export interface DuplicateRequest {
  hint: DuplicateReferenceHint;
  onConfirm: (reason: string) => Promise<void> | void;
}

export function DuplicateReferenceSheet({
  request,
  currency,
  timeZone,
  onClose,
}: {
  request: DuplicateRequest | null;
  currency: string;
  timeZone: string;
  onClose: () => void;
}) {
  return (
    <Sheet open={!!request} onClose={onClose} title="Reference used before">
      {request && (
        <DuplicateForm
          key={`${request.hint.tender_id ?? ''}:${request.hint.reference}`}
          request={request}
          currency={currency}
          timeZone={timeZone}
          onClose={onClose}
        />
      )}
    </Sheet>
  );
}

function DuplicateForm({
  request,
  currency,
  timeZone,
  onClose,
}: {
  request: DuplicateRequest;
  currency: string;
  timeZone: string;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const online = useOnline();
  const error = reason.trim() ? null : 'Say why this reference is taken again';
  const { hint } = request;
  return (
    <div className="d-stack" data-testid="duplicate-reference">
      <Notice tone="warning" role="alert">
        Reference <strong className="d-mono d-break">{hint.reference}</strong> was already used.
        Check the customer’s confirmation before taking it again: a screenshot can be shown twice.
      </Notice>
      <div className="d-list" data-testid="duplicate-matches">
        {hint.matches.map((m) => (
          <div className="d-row" key={`${m.kind}:${m.id}`}>
            <div className="d-row__main">
              <div className="d-row__title">{describeMatch(m)}</div>
              <div className="d-row__meta">
                {m.amount_minor != null ? `${formatMinor(m.amount_minor, currency)} · ` : ''}
                {m.method ? `${m.method} · ` : ''}
                {m.at ? formatInTimeZone(m.at, timeZone) : ''}
                {(m.refunded_minor ?? 0) > 0
                  ? ` · ${formatMinor(m.refunded_minor ?? 0, currency)} refunded`
                  : ''}
              </div>
            </div>
          </div>
        ))}
      </div>
      <p className="d-muted">
        Taking it again is flagged for the owner’s review, with your reason.
      </p>
      <Field
        label="Why it is taken again"
        help="Required; for example: one transfer for two orders."
      >
        {(id) => (
          <Textarea
            id={id}
            rows={2}
            maxLength={DUPLICATE_REASON_MAX}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="duplicate-reason"
          />
        )}
      </Field>
      {touched && error && (
        <p className="d-error" role="alert">
          {error}
        </p>
      )}
      <div className="d-sheet__actions">
        <Button variant="ghost" onClick={onClose} data-testid="duplicate-back">
          Back: check it
        </Button>
        <Button
          variant="danger"
          disabled={!online}
          onClick={() => {
            setTouched(true);
            if (error) return;
            const why = reason.trim();
            onClose();
            void request.onConfirm(why);
          }}
          data-testid="duplicate-confirm"
        >
          Take it anyway
        </Button>
      </div>
    </div>
  );
}
